import { useUserStore } from '@/stores/user';
import axios, { AxiosError } from 'axios';
import { sessionService } from './sessionService';
import { formatISO } from 'date-fns';

interface LoginCredentials {
  login: string;
  password: string;
}

interface MfaLoginResponse {
  token: string;
  url?: string;
}

interface MfaVerifyRequest {
  token: string;
  code: string;
}

interface MfaResetRequest {
  token: string;
}

interface SsoLoginRequest {
  token: string;
}

interface RegisterRequest {
  challenge: string;
  vorname?: string;
  nachname?: string;
  password: string;
  street: string;
  houseNr: string;
  zipCode: string;
  city: string;
  country: string;
}

interface LoginResponse {
  success: boolean;
  token: string;
  mandanten: Array<{ mandant: string; adminKz: boolean }>;
  vorname: string;
  nachname: string;
  email: string;
  id: number;
  status: 'ACTIVE' | 'INACTIVE';
  adminKz: boolean;
  supportKz: boolean;
  pwdResetKz: boolean;
  verbraucherKz: boolean;
  rollen?: Array<{
    name?: string;
    typ: number;
    mandant: string;
  }>;
  standorte?: Array<{
    name?: string;
    nr: string;
    mandant: string;
  }>;
  berechtigungen?: Array<{
    typ: number;
    mandant: string;
  }>;
}

export class AuthService {
  private baseURL = '/api/v1';
  private tokenKey = 'auth_token';
  private tokenTimeKey = 'auth_token_time';
  private mandantKey = 'current_mandant';
  private mandantenKey = 'mandanten';
  private userKey = 'current_user';
  private readonly sessionTimeout = 30 * 60 * 1000; // 30 Minuten
  private readonly tokenValidationInterval = 15 * 60 * 1000; // 15 Minuten
  private readonly activityTimeKey = 'last_activity_time';
  private tokenCheckInterval: number | null = null;
  private activityListenerAttached = false;
  private hasRedirectedToLogin = false; // Globaler Zustand für Weiterleitungsstatus

  constructor() {
    // Initialisiere den Weiterleitungsstatus aus dem sessionStorage
    this.hasRedirectedToLogin = sessionStorage.getItem('redirected_to_login') === 'true';

    this.startTokenValidation();
    this.setupActivityTracking();

    // Abfang-Flag, das weitere API-Anfragen verhindert, wenn wir bereits zum Login weitergeleitet haben
    axios.interceptors.request.use((config) => {
      // Wenn bereits eine Weiterleitung zum Login erfolgt ist, breche alle weiteren API-Anfragen ab
      // (außer Login-bezogene Anfragen)
      const isLoginRequest =
        config.url?.includes('/account/login') ||
        config.url?.includes('/account/mfa/login') ||
        config.url?.includes('/account/mfa/verify');

      if (this.hasRedirectedToLogin && !isLoginRequest) {
        console.log('API-Anfrage während Redirect-Status abgebrochen:', config.url);
        return Promise.reject(new Error('Anfrage abgebrochen während Redirect'));
      }

      const token = this.getToken();
      const mandant = this.getMandant();

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (mandant && config.headers) {
        config.headers['X-Mandant'] = mandant;
      }

      if (config.headers) {
        if (!config.headers.has('Content-Type')) {
          config.headers['Content-Type'] = 'application/json';
        }
        if (!config.headers.has('Accept')) {
          config.headers['Accept'] = 'application/json';
        }
      }

      return config;
    });

    // Debug-Interceptor
    axios.interceptors.request.use((request) => {
      console.log('Starting Request:', {
        url: request.url,
        method: request.method,
        headers: request.headers,
        data: request.data
      });
      return request;
    });

    axios.interceptors.response.use(
      (response) => {
        console.log('Response:', {
          status: response.status,
          headers: response.headers,
          data: response.data
        });
        return response;
      },
      (error) => {
        console.error('Response Error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });

        // Wenn der Token abgelaufen ist (401 Unauthorized)
        if (error.response?.status === 401) {
          // Prüfen, ob es sich um eine Login-Anfrage handelt
          const isLoginRequest =
            error.config?.url?.includes('/account/login') ||
            error.config?.url?.includes('/account/mfa/login') ||
            error.config?.url?.includes('/account/mfa/verify');

          // Nur weiterleiten, wenn es keine Login-Anfrage ist UND noch nicht weitergeleitet wurde
          if (!isLoginRequest && !this.hasRedirectedToLogin) {
            console.log('Token ist abgelaufen oder ungültig - Weiterleitung zur Login-Seite', {
              url: error.config?.url,
              time: formatISO(new Date()),
              lastActivity: formatISO(new Date(this.getLastActivityTime())),
              inactiveTime: (Date.now() - this.getLastActivityTime()) / 1000 + 's'
            });

            // Setze Flag, dass wir bereits weitergeleitet haben
            this.hasRedirectedToLogin = true;
            sessionStorage.setItem('redirected_to_login', 'true');

            this.logout();
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private startTokenValidation() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    // Validiere Token alle 15 Minuten
    this.tokenCheckInterval = window.setInterval(() => {
      // Nur validieren, wenn ein Token existiert
      if (this.hasToken()) {
        this.validateTokenAndSession();
      }
    }, this.tokenValidationInterval);

    // Initial validation
    if (this.hasToken()) {
      this.validateTokenAndSession();
    }
  }

  private setupActivityTracking() {
    if (this.activityListenerAttached) return;

    const updateActivity = () => {
      if (this.hasToken()) {
        sessionStorage.setItem(this.activityTimeKey, Date.now().toString());
      }
    };

    // Aktivität tracken
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        updateActivity();
      }
    });

    // Initiale Aktivität setzen
    updateActivity();
    this.activityListenerAttached = true;
  }

  private getLastActivityTime(): number {
    const timeStr = sessionStorage.getItem(this.activityTimeKey);
    return timeStr ? parseInt(timeStr) : Date.now();
  }

  private async validateTokenAndSession() {
    if (!this.hasToken()) return;

    const currentTime = Date.now();
    const lastActivityTime = this.getLastActivityTime();
    const inactivityTime = currentTime - lastActivityTime;

    // Wenn der Benutzer länger als sessionTimeout inaktiv war, ausloggen
    if (inactivityTime >= this.sessionTimeout) {
      console.log('Benutzer inaktiv - Logout', {
        inactiveTime: Math.floor(inactivityTime / 1000 / 60) + ' Minuten',
        timeout: Math.floor(this.sessionTimeout / 1000 / 60) + ' Minuten'
      });
      await this.logout();
      window.location.href = '/login';
      return;
    }

    // Token-Validierung nur wenn nötig (wenn mehr als 5 Minuten inaktiv)
    if (inactivityTime >= 5 * 60 * 1000) {
      try {
        await sessionService.checkSessionValidity();
        // Wenn die Validierung erfolgreich war, Activity-Zeit aktualisieren
        sessionStorage.setItem(this.activityTimeKey, Date.now().toString());
      } catch (error) {
        console.error('Token validation failed:', error);
        await this.logout();
        window.location.href = '/login';
      }
    }
  }

  async ssoLogin(token: string): Promise<LoginResponse> {
    const response = await axios.post<LoginResponse>(
      `${this.baseURL}/sso/login`,
      { token } as SsoLoginRequest,
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      }
    );

    if (response.data.success && response.data.token) {
      const token = response.data.token.replace('Bearer ', '');
      this.setToken(token);

      if (response.data.mandanten?.length === 1) {
        this.setMandant(response.data.mandanten[0].mandant);
      }

      // Speichere Mandanten
      this.setMandanten(response.data.mandanten);

      // Speichere Benutzerinformationen
      const userStore = useUserStore();
      userStore.setUserInfo(
        response.data.vorname,
        response.data.nachname,
        response.data.email,
        response.data.adminKz,
        response.data.supportKz,
        response.data.verbraucherKz,
        response.data.rollen || [],
        response.data.standorte || []
      );

      // Speichere zusätzliche Benutzerinformationen
      sessionStorage.setItem(
        this.userKey,
        JSON.stringify({
          id: response.data.id,
          vorname: response.data.vorname,
          nachname: response.data.nachname,
          email: response.data.email,
          status: response.data.status,
          adminKz: response.data.adminKz,
          verbraucherKz: response.data.verbraucherKz,
          rollen: response.data.rollen || [],
          standorte: response.data.standorte || [],
          berechtigungen: response.data.berechtigungen || []
        })
      );
    }

    return response.data;
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // Zurücksetzen des Redirect-Status
      this.hasRedirectedToLogin = false;
      sessionStorage.removeItem('redirected_to_login');

      const loginData = {
        login: credentials.login.trim().includes('@')
          ? credentials.login.trim()
          : credentials.login.trim().toUpperCase(),
        password: credentials.password
      };

      console.log('Sending login request:', {
        url: `${this.baseURL}/account/login`,
        data: loginData
      });

      const response = await axios.post<LoginResponse>(`${this.baseURL}/account/login`, loginData, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        },
        withCredentials: true
      });

      console.log('Login response:', response.data);

      if (response.data.success && response.data.token) {
        const token = response.data.token.replace('Bearer ', '');
        this.setToken(token);

        if (response.data.mandanten?.length === 1) {
          this.setMandant(response.data.mandanten[0].mandant);
        }

        // Speichere Mandanten
        this.setMandanten(response.data.mandanten);

        // Speichere Benutzerinformationen
        const userStore = useUserStore();
        userStore.setUserInfo(
          response.data.vorname,
          response.data.nachname,
          response.data.email,
          response.data.adminKz,
          response.data.supportKz,
          response.data.verbraucherKz,
          response.data.rollen || [],
          response.data.standorte || []
        );

        // Speichere zusätzliche Benutzerinformationen
        sessionStorage.setItem(
          this.userKey,
          JSON.stringify({
            id: response.data.id,
            vorname: response.data.vorname,
            nachname: response.data.nachname,
            email: response.data.email,
            status: response.data.status,
            adminKz: response.data.adminKz,
            verbraucherKz: response.data.verbraucherKz,
            rollen: response.data.rollen || [],
            standorte: response.data.standorte || [],
            berechtigungen: response.data.berechtigungen || []
          })
        );
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        console.error('Login failed:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          message: error.message,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
            data: error.config?.data
          }
        });

        const status = error.response.status;
        if (status === 401) {
          throw new Error('Ungültige Anmeldedaten');
        }
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      const token = this.getToken();
      if (token) {
        await axios.post(
          `${this.baseURL}/account/logout`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.stopTokenValidation();
      this.clearToken();
      sessionStorage.removeItem(this.mandantKey);
      sessionStorage.removeItem(this.mandantenKey);
      sessionStorage.removeItem(this.userKey);
    }
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    if (!token) {
      this.clearToken();
      return;
    }

    // Token-Validierung (JWT Format)
    if (!this.isValidJWT(token)) {
      console.error('Invalid token format');
      return;
    }

    sessionStorage.setItem(this.tokenKey, token);
    sessionStorage.setItem(this.tokenTimeKey, Date.now().toString());
    sessionStorage.setItem(this.activityTimeKey, Date.now().toString());
  }

  private isValidJWT(token: string): boolean {
    const jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/;
    return jwtRegex.test(token);
  }

  clearToken(): void {
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.tokenTimeKey);
    sessionStorage.removeItem(this.activityTimeKey);
  }

  getTokenTime(): number {
    const timeStr = sessionStorage.getItem(this.tokenTimeKey);
    return timeStr ? parseInt(timeStr) : 0;
  }

  getRemainingTime(): number {
    if (!this.hasToken()) return 0;
    return sessionService.getRemainingTime();
  }

  getMandant(): string | null {
    const mandant = sessionStorage.getItem(this.mandantKey);
    if (!mandant) {
      console.warn('Kein Mandant gefunden - getMandant liefert null zurück');
      return null;
    }

    console.log('getMandant wird aufgerufen, gespeicherter Mandant:', mandant);
    return mandant.toUpperCase();
  }

  getMandanten(): Array<{ mandant: string; adminKz: boolean }> {
    const mandantenStr = sessionStorage.getItem(this.mandantenKey);
    if (!mandantenStr) {
      console.warn('Keine Mandanten-Liste gefunden');
      return [];
    }
    try {
      const mandanten = JSON.parse(mandantenStr);
      if (!Array.isArray(mandanten)) {
        console.error('Ungültiges Mandanten-Format');
        return [];
      }
      return mandanten.map((m: { mandant: string; adminKz: boolean }) => ({
        ...m,
        mandant: m.mandant.toUpperCase()
      }));
    } catch (error) {
      console.error('Fehler beim Parsen der Mandanten:', error);
      return [];
    }
  }

  setMandant(mandant: string): void {
    if (!mandant) {
      console.error('Ungültiger Mandant - setMandant mit leerem Wert aufgerufen');
      return;
    }

    console.log('setMandant aufgerufen mit:', mandant);

    // Validiere ob der Mandant in der Liste der verfügbaren Mandanten ist
    const verfügbareMandanten = this.getMandanten();
    console.log('Verfügbare Mandanten:', verfügbareMandanten);

    const mandantExists = verfügbareMandanten.some(
      (m) => m.mandant.toUpperCase() === mandant.toUpperCase()
    );

    if (!mandantExists) {
      console.error('Mandant nicht in der Liste der verfügbaren Mandanten:', {
        mandant,
        verfügbareMandanten
      });
      return;
    }

    sessionStorage.setItem(this.mandantKey, mandant.toUpperCase());
    console.log('Mandant erfolgreich gesetzt:', mandant.toUpperCase());
  }

  setMandanten(mandanten: Array<{ mandant: string; adminKz: boolean }>): void {
    if (!Array.isArray(mandanten)) {
      console.error('Ungültiges Mandanten-Format');
      return;
    }

    console.log('setMandanten aufgerufen mit:', mandanten);

    const validatedMandanten = mandanten
      .filter((m) => m && typeof m.mandant === 'string')
      .map((m) => ({
        ...m,
        mandant: m.mandant.toUpperCase()
      }));

    if (validatedMandanten.length === 0) {
      console.error('Keine gültigen Mandanten gefunden');
      return;
    }

    try {
      // Speichere Mandanten im sessionStorage
      const mandantenJSON = JSON.stringify(validatedMandanten);
      sessionStorage.setItem(this.mandantenKey, mandantenJSON);

      // Überprüfe sofort, ob die Daten wirklich gespeichert wurden
      const storedData = sessionStorage.getItem(this.mandantenKey);
      if (!storedData) {
        console.error(
          'Fehler beim Speichern der Mandanten im sessionStorage - Daten wurden nicht gespeichert'
        );
      } else if (storedData !== mandantenJSON) {
        console.error(
          'Fehler beim Speichern der Mandanten im sessionStorage - Daten wurden verfälscht',
          {
            original: mandantenJSON,
            stored: storedData
          }
        );
      } else {
        console.log('Mandanten erfolgreich im sessionStorage gespeichert');
      }
    } catch (error) {
      console.error('Fehler beim Setzen der Mandanten im sessionStorage:', error);
    }

    // Wenn nur ein Mandant verfügbar ist, setze diesen automatisch
    if (validatedMandanten.length === 1) {
      console.log('Nur ein Mandant verfügbar, setze automatisch:', validatedMandanten[0].mandant);
      setTimeout(() => {
        this.setMandant(validatedMandanten[0].mandant);
      }, 100); // Setze mit kurzer Verzögerung, um sicherzustellen, dass die Liste zuerst gespeichert wird
    }
  }

  // Synchrone Prüfung, ob ein Token vorhanden ist (ohne Server-Validierung)
  hasToken(): boolean {
    return !!this.getToken();
  }

  // Asynchrone Prüfung mit Server-Validierung
  async isAuthenticated(): Promise<boolean> {
    // Wenn kein Token vorhanden ist, direkt false zurückgeben
    if (!this.hasToken()) {
      return false;
    }

    try {
      // Validiere Token auf dem Server
      await sessionService.checkSessionValidity();
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      // Bei Fehler das Token löschen
      this.clearToken();
      sessionStorage.removeItem(this.mandantKey);
      sessionStorage.removeItem(this.mandantenKey);
      sessionStorage.removeItem(this.userKey);

      return false;
    }
  }

  isVerbraucher(): boolean {
    const user = this.getCurrentUser();
    return user?.verbraucherKz === true;
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    console.log('Current user data:', user);

    // System Admin Check (adminKz ist ein boolean)
    const isSystemAdmin = user?.adminKz === true;

    // Mandanten Admin Check
    const currentMandant = this.getMandant();
    const currentMandantData = this.getMandanten().find(
      (m) => m.mandant.toUpperCase() === currentMandant?.toUpperCase()
    );
    const isMandantAdmin = currentMandantData?.adminKz === true;

    return isSystemAdmin || isMandantAdmin;
  }

  isSystemAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.adminKz === true;
  }

  isSupport(): boolean {
    // Implementierung der Support-Prüfung
    return false; // Anpassen nach Ihrer Logik
  }

  getRollen() {
    const mandant = this.getMandant();
    const rollen: Array<{ name?: string; typ: number; mandant: string }> =
      this.getCurrentUser()?.rollen || [];
    return rollen.filter((x) => x.mandant?.toUpperCase() === mandant?.toUpperCase());
  }

  getStandorte() {
    const mandant = this.getMandant();
    const standorte: Array<{ nr: string; name?: string; mandant: string }> =
      this.getCurrentUser()?.standorte || [];
    return standorte.filter((x) => x.mandant?.toUpperCase() === mandant?.toUpperCase());
  }

  getBerechtigungen(): Array<{ typ: number; mandant: string }> {
    const mandant = this.getMandant();
    const berechtigungen: Array<{ typ: number; mandant: string }> =
      this.getCurrentUser()?.berechtigungen || [];
    return berechtigungen.filter((x) => x.mandant?.toUpperCase() === mandant?.toUpperCase());
  }

  getCurrentUser() {
    const userStr = sessionStorage.getItem(this.userKey);
    console.debug('Raw user data from sessionStorage:', userStr);
    if (!userStr) {
      console.warn('Keine Benutzerdaten im sessionStorage gefunden');
      return null;
    }
    try {
      const user = JSON.parse(userStr);
      console.debug('Parsed user data:', user);
      return user;
    } catch (error) {
      console.error('Fehler beim Parsen der Benutzerdaten:', error);
      return null;
    }
  }

  async reset2FA(password: string): Promise<void> {
    try {
      await axios.post(
        `${this.baseURL}/account/reset-2fa`,
        { password },
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        }
      );
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Ungültiges Passwort');
        }
      }
      throw error;
    }
  }

  async changePassword(data: { password: string; newPassword: string }): Promise<boolean> {
    try {
      const result = await axios.post(`${this.baseURL}/account/pwd`, data, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      });
      return result.status === 204;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Aktuelles Passwort ist ungültig');
        } else if (status === 400) {
          throw new Error('Das neue Passwort entspricht nicht den Anforderungen');
        }
      }
      throw error;
    }
  }

  async resetUserPassword(login: string, newPassword: string): Promise<void> {
    try {
      // Administratives Zurücksetzen des Passworts ohne Kenntnis des aktuellen Passworts
      await axios.patch(
        `${this.baseURL}/benutzer/${login}/reset`,
        { newPassword },
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Mandant': this.getMandant() || ''
          }
        }
      );
    } catch (error) {
      console.error('Fehler beim Zurücksetzen des Passworts:', error);
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Nicht autorisiert - Bitte melden Sie sich an');
        } else if (status === 403) {
          throw new Error('Keine Berechtigung zum Zurücksetzen von Passwörtern');
        } else if (status === 400) {
          throw new Error('Das neue Passwort entspricht nicht den Anforderungen');
        } else if (status === 404) {
          throw new Error('Benutzer nicht gefunden');
        }
      }
      throw error;
    }
  }

  async resetUserMfa(login: string): Promise<void> {
    try {
      await axios.patch(
        `${this.baseURL}/benutzer/${login}/mfa/reset`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Mandant': this.getMandant() || ''
          }
        }
      );
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Nicht autorisiert - Bitte melden Sie sich an');
        } else if (status === 403) {
          throw new Error('Keine Berechtigung zum Zurücksetzen der 2FA');
        } else if (status === 404) {
          throw new Error('Benutzer nicht gefunden');
        }
      }
      throw error;
    }
  }

  async resetSelfMfa(): Promise<void> {
    try {
      // 1. Versuche Benutzerkennung aus dem Session Storage zu laden
      const currentUser = this.getCurrentUser();

      // 2. JWT-Token dekodieren um den Benutzernamen zu extrahieren
      const token = this.getToken();
      if (!token) {
        throw new Error('Kein gültiges Token vorhanden - Bitte melden Sie sich erneut an');
      }

      // Token-Teile aufteilen
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Ungültiges Token-Format');
      }

      // Payload dekodieren
      let payload;
      try {
        // Base64Url zu regulärem Base64 konvertieren und dann dekodieren
        const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        const decodedPayload = atob(base64Payload);
        payload = JSON.parse(decodedPayload);
      } catch (e) {
        console.error('Fehler beim Dekodieren des JWT:', e);
        throw new Error('Token konnte nicht dekodiert werden');
      }

      // Benutzerkennung aus dem Token extrahieren (meist unter 'sub' oder 'unique_name')
      const userIdentifier =
        payload.sub ||
        payload.unique_name ||
        (currentUser && (currentUser.login || currentUser.email));

      if (!userIdentifier) {
        throw new Error('Benutzerkennung konnte nicht ermittelt werden');
      }

      console.log('Setze 2FA zurück für Benutzer:', userIdentifier);

      // Da es keinen speziellen Endpunkt für Selbstrücksetzung gibt,
      // verwenden wir den Admin-Endpunkt mit dem eigenen Login
      await axios.patch(
        `${this.baseURL}/benutzer/${userIdentifier}/mfa/reset`,
        {}, // Kein Request-Body benötigt
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Mandant': this.getMandant() || ''
          }
        }
      );
    } catch (error) {
      console.error('Fehler beim Zurücksetzen der eigenen 2FA:', error);
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Nicht autorisiert - Bitte melden Sie sich an');
        } else if (status === 403) {
          throw new Error('Keine Berechtigung zum Zurücksetzen der 2FA');
        } else if (status === 404) {
          throw new Error('Benutzer nicht gefunden - Bitte kontaktieren Sie den Administrator');
        }
      }
      throw error;
    }
  }

  async mfaLogin(credentials: LoginCredentials): Promise<MfaLoginResponse> {
    try {
      const loginData = {
        login: credentials.login.trim().includes('@')
          ? credentials.login.trim()
          : credentials.login.trim().toUpperCase(),
        password: credentials.password
      };

      const response = await axios.post<MfaLoginResponse>(
        `${this.baseURL}/account/mfa/login`,
        loginData,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
          }
        }
      );

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Ungültige Anmeldedaten');
        }
      }
      throw error;
    }
  }

  async mfaVerify(token: string, code: string): Promise<LoginResponse> {
    try {
      // Zurücksetzen des Redirect-Status
      this.hasRedirectedToLogin = false;
      sessionStorage.removeItem('redirected_to_login');

      // Validierung: Code muss 6-stellig numerisch sein (entsprechend der TOTP-Anforderungen)
      if (!code || !/^\d{6}$/.test(code)) {
        throw new Error(
          'Ungültiger Code. Bitte geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.'
        );
      }

      const response = await axios.post<LoginResponse>(
        `${this.baseURL}/account/mfa/verify`,
        { token, code } as MfaVerifyRequest,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        }
      );

      // Prüfen, ob die Antwort erfolgreich war, bevor Tokens gesetzt werden
      if (response.data.success && response.data.token) {
        const authToken = response.data.token.replace('Bearer ', '');

        // Setze Token und speichere es persistent
        this.setToken(authToken);

        // Setze Mandanten
        this.setMandanten(response.data.mandanten);

        // Setze Mandant wenn nur einer vorhanden
        if (response.data.mandanten?.length === 1) {
          const mandant = response.data.mandanten[0].mandant;
          this.setMandant(mandant);
        }

        const userStore = useUserStore();
        userStore.setUserInfo(
          response.data.vorname,
          response.data.nachname,
          response.data.email,
          response.data.adminKz,
          response.data.supportKz,
          response.data.verbraucherKz,
          response.data.rollen || [],
          response.data.standorte || []
        );

        sessionStorage.setItem(
          this.userKey,
          JSON.stringify({
            id: response.data.id,
            vorname: response.data.vorname,
            nachname: response.data.nachname,
            email: response.data.email,
            status: response.data.status,
            adminKz: response.data.adminKz,
            verbraucherKz: response.data.verbraucherKz,
            rollen: response.data.rollen || [],
            standorte: response.data.standorte || [],
            berechtigungen: response.data.berechtigungen || []
          })
        );
      } else if (!response.data.success) {
        // Explizite Fehlerbehandlung, wenn der Server success=false zurückgibt
        throw new Error('Der Code konnte nicht validiert werden. Bitte versuchen Sie es erneut.');
      }

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Ungültiger Code oder abgelaufener Token');
        } else if (status === 400) {
          throw new Error('Ungültiger Code. Bitte geben Sie einen gültigen 6-stelligen Code ein.');
        } else if (status >= 500) {
          throw new Error('Server-Fehler. Bitte versuchen Sie es später erneut.');
        }
      }
      throw error;
    }
  }

  async mfaReset(token: string): Promise<MfaLoginResponse> {
    try {
      const response = await axios.post<MfaLoginResponse>(
        `${this.baseURL}/account/mfa/reset`,
        { token } as MfaResetRequest,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        }
      );

      return response.data;
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Ungültiger oder abgelaufener Token');
        }
      }
      throw error;
    }
  }

  private stopTokenValidation() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  // Öffentliche Methode zum harten Zurücksetzen des Redirect-Status
  resetRedirectStatus(): void {
    this.hasRedirectedToLogin = false;
    sessionStorage.removeItem('redirected_to_login');
    console.log('Redirect-Status zurückgesetzt');
  }

  async register(request: RegisterRequest): Promise<boolean> {
    try {
      const response = await axios.post(`${this.baseURL}/account/register`, request, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.status === 204;
    } catch {
      return false;
    }
  }
}

export const authService = new AuthService();
